<template>
  <b-card>
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h4 class="mb-0">
        {{ $t('Create New Question') }}
      </h4>
    </div>

    <!-- BODY -->
    <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
      <!-- Form -->
      <b-form @submit.prevent="handleSubmit(handleQuestionSubmit)" @reset.prevent="resetForm">
        <!-- Username -->
        <validation-provider #default="validationContext" :name="$t('survey.question')" rules="required" vid="question"
          :custom-messages="{ required: $t('survey.validation.required', { _field_: $t('survey.question') }) }">
          <b-form-group :label="$t('survey.question')" label-for="SurveyCode"
            :state="getValidationState(validationContext)">
            <input v-model="questionText" type="hidden" style="display: none;" />
            <div
              :class="{ 'is-invalid': validationContext.errors[0], 'is-valid': validationContext.valid && validationContext.validated }">
              <quill-editor height="100" :options="snowOption" :content="localQuestion.description"
                @change="onEditorChange($event)" @blur="onEditorBlur($event)" @focus="onEditorFocus($event)"
                @ready="onEditorReady($event)" />
            </div>

            <div class="invalid-feedback" :class="{ 'd-block': validationContext.errors[0] }">
              {{ validationContext.errors[0] }}
            </div>
          </b-form-group>
        </validation-provider>

        <b-form-group :label="$t('survey.comment')" label-for="SurveyCode">
          <b-form-checkbox v-model="localQuestion.allowComment" value="true">
            {{ $t('survey.surveyquestion.comment') }}
          </b-form-checkbox>
        </b-form-group>

        <b-form-group :label="$t('survey.required.required')" label-for="SurveyCode">
          <b-form-checkbox v-model="localQuestion.required" value="true">
            {{ $t('survey.surveyquestion.required') }}
          </b-form-checkbox>
        </b-form-group>
        <!-- User Role -->

        <validation-provider #default="validationContext" :name="$t('survey.surveyquestion.question_tag')"
          rules="required" vid="questionTag"
          :custom-messages="{ required: $t('survey.validation.required', { _field_: $t('survey.surveyquestion.question_tag') }) }">
          <b-form-group :label="$t('survey.surveyquestion.question_tag')" label-for="user-role"
            :state="getValidationState(validationContext)">
            <input :value="questionTag ? (questionTag.title || questionTag) : null" type="hidden"
              style="display: none;" />
            <div
              :class="{ 'is-invalid': validationContext.errors[0], 'is-valid': validationContext.valid && validationContext.validated }">
              <question-tag-picker :initialtag="questionTag" :survey-info="surveyInfo" :key="surveyChecker"
                @selectionChanged="QuestionTagUpdated" />
            </div>
            <div class="invalid-feedback" :class="{ 'd-block': validationContext.errors[0] }">
              {{ validationContext.errors[0] }}
            </div>
          </b-form-group>
        </validation-provider>

        <!-- Rating Options - Always visible -->
        <b-form-group :label="$t('survey.surveyquestion.value_text_min')" label-for="user-role">
          <b-col cols="12">
            <b-row v-for="(option, index) in ratingOptions" :key="index" class="mb-1">
              <b-col cols="3">
                <b-form-input :value="option.value" disabled class="text-center" :placeholder="String(option.value)"
                  trim />
              </b-col>
              <b-col>

                <validation-provider #default="validationContext"
                  :name="`${$t('survey.surveyquestion.option')} ${option.value}`" rules="required"
                  :vid="`ratingOption${index}`"
                  :custom-messages="{ required: $t('survey.validation.required', { _field_: $t('survey.surveyquestion.option') + ' ' + option.value }) }">
                  <b-form-input v-model="option.text" :placeholder="getDefaultPlaceholder(option.value)"
                    :state="getValidationState(validationContext)" trim />
                  <div class="invalid-feedback" :class="{ 'd-block': validationContext.errors[0] }">
                    {{ validationContext.errors[0] }}
                  </div>
                </validation-provider>
              </b-col>
              <b-col cols="auto" v-if="ratingOptions.length > 2">
                <b-button variant="outline-danger" size="sm" @click="removeRatingOption(index)">
                  <feather-icon icon="XIcon" size="14" />
                </b-button>
              </b-col>
              
            </b-row>

            <b-row v-if="ratingOptions.length < 10" class="mt-2">
              <b-col>
                <b-button variant="outline-primary" size="sm" block @click="addRatingOption">
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  {{ $t('survey.surveyquestion.add_option') }}
                </b-button>
              </b-col>
            </b-row>
          </b-col>
        </b-form-group>
        <!-- Form Actions -->
        <div class="d-flex mt-2">
          <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" class="mr-2" type="submit">
            {{ $t("Add") }}
          </b-button>
          <b-button v-ripple.400="'rgba(186, 191, 199, 0.15)'" type="button" variant="outline-secondary"
            @click="goBack">
            {{ $t("Cancel") }}
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </b-card>
</template>

<script>
import {
  BFormCheckbox,
  BFormTextarea,
  BFormRating,
  BRow,
  BCol,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BFormRadioGroup,
  BFormRadio,
  BFormInvalidFeedback,
  BFormValidFeedback,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { ref, toRefs, watch, inject, onMounted, onUnmounted } from "@vue/composition-api";
import { required, alphaNum, email } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import countries from "@/@fake-db/data/other/countries";
import store from "@/store";
import router from "@/router";

import { quillEditor } from "vue-quill-editor";
import SurveyTypePicker from "../components/SurveyQuestionTypePicker.vue";
import QuestionTagPicker from "../components/QuestionTagPicker.vue";
import SurveyThemePicker from "../components/SurveyThemePicker.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import userStoreModule from "../surveyquestionStoreModule";

import { heightTransition } from "@core/mixins/ui/transition";
export default {
  components: {
    BFormCheckbox,
    BFormTextarea,
    BFormRating,
    BRow,
    BCol,
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BFormRadioGroup,
    BFormRadio,
    BFormInvalidFeedback,
    BFormValidFeedback,
    vSelect,
    SurveyTypePicker,
    QuestionTagPicker,
    SurveyThemePicker,
    quillEditor,
    ToastificationContent,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  mixins: [heightTransition],
  data() {
    return {
      required,
      surveyChecker: 0,
      alphaNum,
      email,
      countries,
      minValue: 1,
      currentId: 0,
      selectedCategory: null,
      questionTag: null,
      questionTypeKey: "",
      questionText: "",
      isUpdated: false,
      maxValue: 10,
      value: null,
      options: [],
      questionCount: 0,
      ratingOptions: [
        { value: 1, text: '' },
        { value: 2, text: '' },
        { value: 3, text: '' },
        { value: 4, text: '' },
        { value: 5, text: '' }
      ],
      snowOption: {
        theme: "snow",
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],

            [{ color: [] }, { background: [] }],
            ["clean"],
          ],
        },
      },
      localQuestion: {
        required: true,
        title: "",
        examid: "",
        description: "",
        questionType: "",
        questionTypeKey: "",
        allowComment: false,
        acceptedAnswers: [],
        acceptedAnswer: null,
        questionAnswers: [
          {
            id: 1,
            selected: false,
            value: this.generateUUID(),
            answer: null,
          },
        ],
        additionalInfo: {
          min1Value: 1,
          min2Value: 2,
          min3Value: 3,
          min4Value: 4,
          min5Value: 5,

          min1Text: "Poor",
          min2Text: "Needs Improvement",
          min3Text: "Average",
          min4Text: "Good",
          min5Text: "Excellent",

          maxValue: 5,
          minText: "Bad",
          maxText: "Amazing",
        },
      },
    };
  },
  mounted() {
    this.initTrHeight();
    // Fetch question count
    if (this.fetchQuestionCount) {
      this.fetchQuestionCount(this);
    }
  },
  created() {
    window.addEventListener("resize", this.initTrHeight);
  },
  destroyed() {
    window.removeEventListener("resize", this.initTrHeight);
  },
  computed: {
    state() {
      return Boolean(this.value);
    },
    answers() {
      //console.log("---");
      //console.log(this.localQuestion.questionAnswers);

      return JSON.parse(JSON.stringify(this.localQuestion.questionAnswers));
    },
  },
  watch: {
    ratingOptions: {
      handler(newVal) {
        // Auto-sync rating options to additionalInfo when changed
        this.syncRatingOptionsToAdditionalInfo();
      },
      deep: true
    }
  },
  methods: {
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },

    // Rating options methods
    addRatingOption() {
      if (this.ratingOptions.length < 10) {
        const newValue = this.ratingOptions.length + 1;
        this.ratingOptions.push({ value: newValue, text: '' });
        this.syncRatingOptionsToAdditionalInfo();
      }
    },

    removeRatingOption(index) {
      if (this.ratingOptions.length > 2) {
        this.ratingOptions.splice(index, 1);
        // Re-index values
        this.ratingOptions.forEach((option, idx) => {
          option.value = idx + 1;
        });
        this.syncRatingOptionsToAdditionalInfo();
      }
    },

    getDefaultPlaceholder(value) {
      const placeholders = {
        1: 'Poor',
        2: 'Needs Improvement',
        3: 'Average',
        4: 'Good',
        5: 'Excellent'
      };
      return placeholders[value] || `Option ${value}`;
    },

    syncRatingOptionsToAdditionalInfo() {
      // Convert ratingOptions array to additionalInfo object format
      const info = {};

      // Add min values and texts
      this.ratingOptions.forEach((option, index) => {
        const key = index + 1;
        info[`min${key}Value`] = option.value;
        info[`min${key}Text`] = option.text;
      });

      // Add additional fields
      info.maxValue = this.ratingOptions.length;
      info.minText = this.ratingOptions[0]?.text || "Bad";
      info.maxText = this.ratingOptions[this.ratingOptions.length - 1]?.text || "Amazing";

      this.localQuestion.additionalInfo = info;
    },

    loadRatingOptionsFromAdditionalInfo(additionalInfo) {
      // Convert additionalInfo object to ratingOptions array
      const options = [];
      let index = 1;
      while (additionalInfo[`min${index}Value`] !== undefined) {
        options.push({
          value: additionalInfo[`min${index}Value`] || index,
          text: additionalInfo[`min${index}Text`] || ''
        });
        index++;
      }
      // If no options found, initialize with default 5 options
      if (options.length === 0) {
        options.push(
          { value: 1, text: '' },
          { value: 2, text: '' },
          { value: 3, text: '' },
          { value: 4, text: '' },
          { value: 5, text: '' }
        );
      }
      this.ratingOptions = options;
    },
    resetForm() {
      this.questionText = '';
      this.questionTag = null;
      // Reset rating options to default 5 options
      this.ratingOptions = [
        { value: 1, text: '' },
        { value: 2, text: '' },
        { value: 3, text: '' },
        { value: 4, text: '' },
        { value: 5, text: '' }
      ];
    },
    generateUUID() {
      // Public Domain/MIT
      var d = new Date().getTime(); //Timestamp
      var d2 =
        (typeof performance !== "undefined" &&
          performance.now &&
          performance.now() * 1000) ||
        0; //Time in microseconds since page-load or 0 if unsupported
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (
        c
      ) {
        var r = Math.random() * 16; //random number between 0 and 16
        if (d > 0) {
          //Use timestamp until depleted
          r = (d + r) % 16 | 0;
          d = Math.floor(d / 16);
        } else {
          //Use microseconds since page-load if supported
          r = (d2 + r) % 16 | 0;
          d2 = Math.floor(d2 / 16);
        }
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      });
    },
    repeateAgain() {
      this.localQuestion.questionAnswers.push({
        id: 1,
        selected: false,
        value: this.generateUUID(),
        answer: "",
      });

      this.$nextTick(() => {
        this.trAddHeight(this.$refs.row[0].offsetHeight);
      });
    },
    removeItem(index) {
      var items = this.localQuestion.questionAnswers;

      //        var result = items.filter(obj => {
      //   return obj.value=== item
      // })

      items.splice(index, 1);
      this.trTrimHeight(this.$refs.row[0].offsetHeight);
    },
    initTrHeight() {
      this.trSetHeight(null);
      this.$nextTick(() => {
        // this.trSetHeight(this.$refs.form.scrollHeight)
      });
    },
    goBack() {
      const surveyId = router.currentRoute.query.s || store.getters["app/currentUnit"];
      router.push({ name: 'SurveyQuestions', query: { s: surveyId } });
    },
    handleQuestionSubmit() {
      // Sync rating options to additionalInfo before submitting
      this.syncRatingOptionsToAdditionalInfo();

      const surveyId = router.currentRoute.query.s || store.getters["app/currentUnit"];
      this.localQuestion.surveyid = surveyId;
      this.localQuestion.questionOrder = this.questionCount + 1;

      // Set question type to Range Selection for all surveys
      this.localQuestion.questionType = "Range Question";
      this.localQuestion.questionTypeKey = "RANGE_SELECTION";
      this.selectedCategory = {
        questionType: "Range Question",
        questionTypeKey: "RANGE_SELECTION",
      };

      // Only add, no update
      const action = store.dispatch("app-survey-questions/addSurveyQuestion", this.localQuestion);

      action
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('Success'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          });

          // Navigate back to questions list
          router.push({ name: 'SurveyQuestions', query: { s: surveyId } });
        })
        .catch((error) => {
          console.error("Error saving survey question:", error);

          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('survey.surveyattendee.error'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: this.$t(`${error.response?.data?.message}`) || this.$t(`${error.message}`) || this.$t('survey.error_adding_question')
            },
          });
        });
    },

    QuestionTagUpdated(payload) {
      //console.log("question result in info ");
      //console.log(payload);
      if (payload == null || payload == "" || payload == undefined) {
        this.questionTag = null;
        this.questionTagType = null;
        (this.localQuestion.questionTag = null),
          (this.localQuestion.questionTagKey = null);
      } else {
        //console.log("question type  data updated ");
        //console.log(payload.typekey);

        this.questionTagType = payload.typekey;
        this.questionTag = payload.title;
        this.questionTag = payload;

        (this.localQuestion.questionTag = payload.title),
          (this.localQuestion.questionTagType = payload.typekey);
      }
    },

    SurveyTypeUpdated(payload) {
      //console.log("question result in info ");
      //console.log(payload);
      if (payload == null) {
        this.questionTypeKey = null;
        this.questionType = null;
        (this.localQuestion.questionType = null),
          (this.localQuestion.questionTypeKey = null);
      } else {
        //console.log("question type  data updated "+payload.typekey);

        this.questionTypeKey = payload.typekey;
        this.questionType = payload.title;
        this.selectedCategory = payload;

        (this.localQuestion.questionType = payload.title),
          (this.localQuestion.questionTypeKey = payload.typekey);

        if (payload.typekey == "YES_NO_QUESTION") {
          this.localQuestion.questionAnswers = [
            {
              id: 1,
              selected: false,
              value: "YES",
              answer: "YES",
            },
            {
              id: 2,
              selected: false,
              value: "NO",
              answer: "NO",
            },
          ];
        }
      }
    },
    onEditorBlur(quill) {
      //    //console.log('editor blur!', quill)
    },
    onEditorFocus(quill) {
      //   //console.log('editor focus!', quill)
    },
    onEditorReady(quill) {
      //     //console.log('editor ready!', quill)
    },
    onEditorChange({ quill, html, text }) {
      //     //console.log('editor change!', quill, html, text)
      this.isUpdated = true;
      this.localQuestion.description = html;
      this.questionText = text.trim();
    },
    loadQuestionData(question) {
      if (!question) return;

      this.localQuestion = JSON.parse(JSON.stringify(question));

      // Parse questionAnswers if it's a string
      if (typeof question.questionAnswers === 'string') {
        this.localQuestion.questionAnswers = JSON.parse(question.questionAnswers);
      }

      // Parse additionalInfo if it's a string
      if (typeof question.additionalInfo === 'string') {
        this.localQuestion.additionalInfo = JSON.parse(question.additionalInfo);
      }

      this.questionTag = question.questionTag;
      this.selectedCategory = question.questionTypeKey;

      // Set questionText from description
      if (question.description) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = question.description;
        this.questionText = (tempDiv.textContent || tempDiv.innerText || '').trim();
      } else {
        this.questionText = '';
      }

      // Load rating options from additionalInfo
      if (this.localQuestion.additionalInfo) {
        this.loadRatingOptionsFromAdditionalInfo(this.localQuestion.additionalInfo);
      }
    },
  },
  setup() {
    const USER_APP_STORE_MODULE_NAME = "app-survey-questions";

    // Register module
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) {
      store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule);
    }

    const surveyId = ref(router.currentRoute.query.s || store.getters["app/currentUnit"]);
    const surveyInfo = ref(null);

    // Fetch survey info
    if (surveyId.value) {
      store.commit("app/SETACTIVEUNIT", surveyId.value);
      store
        .dispatch("app-survey-questions/fetchCourse", { id: surveyId.value })
        .then((response) => {
          surveyInfo.value = response.data.data;
        })
        .catch((error) => {
          console.error("Error fetching survey:", error);
        });
    }

    // Return function to fetch question count - will be called in mounted
    const fetchQuestionCount = (componentInstance) => {
      if (surveyId.value) {
        store
          .dispatch("app-survey-questions/fetchSurveyQuestions", { id: surveyId.value })
          .then((response) => {
            if (response.data && response.data.data && componentInstance) {
              componentInstance.questionCount = response.data.data.length || 0;
            }
          })
          .catch((error) => {
            console.error("Error fetching questions:", error);
          });
      }
    };

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(USER_APP_STORE_MODULE_NAME);
      }
    });

    return {
      surveyInfo,
      fetchQuestionCount,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

#add-new-user-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

@import "@core/scss/vue/libs/quill.scss";

.ql-editor {
  min-height: 100px;
}

.is-invalid .quill-editor .ql-container {
  border-color: #ea5455 !important;
}

.is-invalid .quill-editor .ql-toolbar {
  border-color: #ea5455 !important;
}

.is-valid .quill-editor .ql-container {
  border-color: #28c76f !important;
}

.is-valid .quill-editor .ql-toolbar {
  border-color: #28c76f !important;
}

// Question Tag Picker validation styles
.is-invalid .v-select .vs__dropdown-toggle {
  border-color: #ea5455 !important;
}

.is-valid .v-select .vs__dropdown-toggle {
  border-color: #28c76f !important;
}
</style>
